import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { LogService } from "@shared/services/log.service";
import { ToasterService } from "@shared/services/toaster.service";
import { AdminNebApiService } from "@shared/services/admin/admin-neb-api.service";
import { AdminUpstreamApiService } from "@shared/services/admin/admin-upstream-api.service";
import { AdminAppserviceApiService } from "@shared/services/admin/admin-appservice-api.service";
import { FE_Appservice, FE_NebConfiguration, FE_Upstream } from "@shared/models/admin-responses";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
    AdminNebAppserviceConfigComponent,
    AppserviceConfigDialogContext
} from "./appservice-config/appservice-config.component";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { SpinnerComponent } from "../../elements/spinner/spinner.component";
import { IboxComponent } from "../../elements/ibox/ibox.component";


@Component({
    templateUrl: "./neb.component.html",
    styleUrls: ["./neb.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SpinnerComponent, IboxComponent, RouterLink, TranslatePipe]
})
export class AdminNebComponent implements OnInit {

    public isLoading = true;
    public isAddingModularNeb = false;
    public hasModularNeb = false;
    public upstreams: FE_Upstream[];
    public appservices: FE_Appservice[];
    public configurations: FE_NebConfiguration[];

    constructor(
        private readonly nebApi: AdminNebApiService,
        private readonly upstreamApi: AdminUpstreamApiService,
        private readonly appserviceApi: AdminAppserviceApiService,
        private readonly toaster: ToasterService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly modal: NgbModal,
        public translate: TranslateService,
        private readonly logService: LogService,
    ) {
        this.translate = translate;
    }

    ngOnInit() {
        this.reload().then(() => this.isLoading = false).catch(error => {
            this.logService.error(error);
            this.translate.get('Error loading go-neb configuration').subscribe((res: string) => {
                this.toaster.pop("error", res);
            });
        });
    }

    private reload(): Promise<any> {
        return Promise.all([
            this.loadAppservices(),
            this.loadConfigurations(),
            this.loadUpstreams(),
        ]);
    }

    private loadUpstreams(): Promise<any> {
        return this.upstreamApi.getUpstreams().then(upstreams => {
            this.upstreams = upstreams;
        });
    }

    private loadAppservices(): Promise<any> {
        return this.appserviceApi.getAppservices().then(appservices => {
            this.appservices = appservices;
        });
    }

    private loadConfigurations(): Promise<any> {
        return this.nebApi.getConfigurations().then(nebConfigs => {
            this.configurations = nebConfigs;
            this.isLoading = false;

            this.hasModularNeb = false;
            for (const neb of this.configurations) {
                if (neb.upstreamId) {
                    this.hasModularNeb = true;
                    break;
                }
            }
        });
    }

    public showAppserviceConfig(neb: FE_NebConfiguration) {
        const selfhostedRef = this.modal.open(AdminNebAppserviceConfigComponent, {
            backdrop: 'static',
            size: 'lg',
        });
        const selfhostedInstance = selfhostedRef.componentInstance as AppserviceConfigDialogContext;
        selfhostedInstance.neb = neb;
    }

    public getEnabledBotsString(neb: FE_NebConfiguration): string {
        const result = neb.integrations.filter(i => i.isEnabled).map(i => i.displayName).join(", ");
        if (!result) return "None";
        return result;
    }

    public addSelfHostedNeb() {
        this.router.navigate(["new", "selfhosted"], {relativeTo: this.activatedRoute});
    }

    public addModularHostedNeb() {
        this.isAddingModularNeb = true;
        const createNeb = (upstream: FE_Upstream) => {
            return this.nebApi.newUpstreamConfiguration(upstream).then(neb => {
                this.configurations.push(neb);
                this.translate.get(['matrix.org\'s go-neb added', 'Click the pencil icon to enable the bots.']).subscribe((res: string) => {
                    this.toaster.pop("success", res[0], res[1]);
                });
                this.isAddingModularNeb = false;
                this.hasModularNeb = true;
            }).catch(error => {
                this.logService.error(error);
                this.isAddingModularNeb = false;
                this.translate.get('Error adding matrix.org\'s go-neb').subscribe((res: string) => {
                    this.toaster.pop("error", res);
                });
            });
        };

        const vectorUpstreams = this.upstreams.filter(u => u.type === "vector");
        if (vectorUpstreams.length === 0) {
            this.logService.debug("Creating default scalar upstream");
            const scalarUrl = "https://scalar.vector.im/api";
            this.upstreamApi.newUpstream("modular", "vector", scalarUrl, scalarUrl).then(upstream => {
                this.upstreams.push(upstream);
                createNeb(upstream);
            }).catch(err => {
                this.logService.error(err);
                this.translate.get('Error creating matrix.org go-neb').subscribe((res: string) => {
                    this.toaster.pop("error", res);
                });
            });
        } else createNeb(vectorUpstreams[0]);
    }
}

